import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { unlistedDiscriminantSchema } from "../../../../shared/core/discriminated-union-read.schema";
import { enumReadSchema } from "../../../../shared/core/enum-read.schema";
import { VibeAllowedRegionSchema } from "../../../../shared/domain/vibe/allowed-regions";
import {
  resolveVibeClusterCondition,
  VibeClusterConditionSchema
} from "../../../../shared/domain/vibe/tenant-cluster-customer-condition";
import {
  VibeTelemetryStatusReadSchema,
  VibeTenantClusterStatusReadSchema
} from "./status-read.schemas";

export const VibeTenantClusterStatusSchema = z.enum(DbEnum.VIBE_TENANT_CLUSTER_STATUS_VALUES);
export const VibeGitHostStatusSchema = z.enum(DbEnum.VIBE_GIT_HOST_STATUS_VALUES);
export const VibeTelemetryStatusSchema = z.enum(DbEnum.VIBE_TELEMETRY_STATUS_VALUES);

/**
 * The git host's status as a reader parses it. A label only — nothing a reader
 * offers or withholds hangs on it — so a value a newer backend reports reads as
 * the server's own word instead of failing the cluster read.
 */
export const VibeGitHostStatusReadSchema = enumReadSchema(DbEnum.VIBE_GIT_HOST_STATUS_VALUES);

/**
 * Org-facing health view of the tenant's dedicated cluster. Deliberately
 * narrow — lifecycle status, the customer-safe condition, and the
 * git-host/telemetry convergence statuses. The cluster's `statusReason` never
 * crosses this boundary: it is Pulumi resource names and provider bodies, and
 * `condition` is the customer's reading of it.
 */
const VibeClusterHealthObjectSchema = z.object({
  status: VibeTenantClusterStatusReadSchema,
  /// Fault, routine update pending, or nominal — derived from the same
  /// resolver as the operator roster, with one customer sentence per kind.
  condition: VibeClusterConditionSchema,
  /// The agent's last-reported git-host (Forgejo) convergence status.
  /// Null until the agent first reports one.
  gitHostStatus: VibeGitHostStatusReadSchema.nullable(),
  /// The agent's last-reported telemetry-stack (collector + Loki) convergence
  /// status. Null until the agent first reports one. `.catch(null)`: the
  /// frontend runtime-parses responses and deploys independently of the
  /// backend (Vercel vs Porter) — during the skew window an older backend
  /// omits this key, which must degrade to "unknown", not fail the whole
  /// response (that would also suppress the DEGRADED banner).
  telemetryStatus: VibeTelemetryStatusReadSchema.nullable().catch(null)
});

/**
 * An older backend serves `statusReason` and no `condition`. The console
 * deploys independently of it (Vercel vs Porter), and a required key it never
 * sends would fail the whole read — which also hides the fault banner. So a
 * response without a condition has one derived here, by the same resolver the
 * backend uses, and the raw reason is then stripped by the object above: it
 * reaches no component either way.
 *
 * debt: transitional — exists only for responses from a backend older than the
 *       `condition` field.
 *       Upgrade trigger: the production backend serves `condition` on
 *       GET /api/vibe/cluster — then delete this preprocess.
 */
function deriveConditionFromAnOlderBackend(input: unknown): unknown {
  if (typeof input !== "object" || input === null || "condition" in input) return input;
  const status = VibeTenantClusterStatusReadSchema.safeParse(
    "status" in input ? input.status : undefined
  );
  if (!status.success) return input;
  const fields: Readonly<Record<string, unknown>> = { ...input };
  const text = (key: string): string | null =>
    typeof fields[key] === "string" ? String(fields[key]) : null;
  return {
    ...input,
    condition: resolveVibeClusterCondition({
      status: status.data,
      statusReason: text("statusReason"),
      gitHostStatus: text("gitHostStatus"),
      telemetryStatus: text("telemetryStatus"),
      // An older backend sends no pause, so the sentence claims nothing about
      // when anything applies or is repaired.
      reconcilePaused: null
    })
  };
}

export const VibeClusterHealthSchema = z.preprocess(
  deriveConditionFromAnOlderBackend,
  VibeClusterHealthObjectSchema
);

export type VibeClusterHealthDto = z.infer<typeof VibeClusterHealthSchema>;

/**
 * `cluster: null` means the org has no dedicated tenant cluster (it runs on
 * shared infrastructure) — nothing to surface, not an error.
 */
export const GetVibeClusterResponseSchema = z.object({
  cluster: VibeClusterHealthSchema.nullable()
});

/**
 * The org opts itself into a dedicated cluster. `region` is immutable for the
 * cluster's lifetime — relocating means a full teardown and re-provision — so
 * the caller states it explicitly rather than inheriting a default that would
 * be expensive to have guessed wrong.
 */
export const ProvisionVibeClusterBodySchema = z
  .object({
    region: VibeAllowedRegionSchema
  })
  .strict();

/**
 * Outcome of an org's opt-in. Mirrors `ProvisionTenantClusterOutcome`.
 * Provision is purely declarative — it records desired state and the
 * reconcile loop converges it — so there is no synchronous failure outcome.
 *
 *   - provisioning: a cluster row is PROVISIONING. `reprovisioned` = true
 *     when a retired cluster was revived in place rather than created fresh.
 *   - already_active: a cluster already exists and is not retired; the
 *     request was a no-op and `status` says where it actually is.
 */
export const ProvisionVibeClusterOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("provisioning"),
    reprovisioned: z.boolean(),
    /**
     * Present and true only when this request declared NOTHING — it found a
     * row already PROVISIONING and reused it. Absent on a fresh create and on
     * a reprovision, so an existing caller reads the outcome unchanged.
     *
     * Optional rather than required because the arm is plain `z.object`, which
     * STRIPS unknown keys: without it declared here the backend's field is
     * dropped before any component sees it, and a repeated request stays
     * indistinguishable from a fresh one on this surface.
     */
    reusedExistingRow: z.boolean().optional()
  }),
  z.object({
    kind: z.literal("already_active"),
    status: VibeTenantClusterStatusReadSchema
  })
]);

/**
 * The outcome as a READER parses it: an outcome a newer backend added reads as
 * the server's own word instead of failing the provision response.
 */
export const ProvisionVibeClusterOutcomeReadSchema = z.union([
  ProvisionVibeClusterOutcomeSchema,
  z.looseObject({ kind: unlistedDiscriminantSchema(ProvisionVibeClusterOutcomeSchema) })
]);

export const ProvisionVibeClusterResponseSchema = z.object({
  outcome: ProvisionVibeClusterOutcomeReadSchema
});

export type ProvisionVibeClusterBody = z.infer<typeof ProvisionVibeClusterBodySchema>;
export type ProvisionVibeClusterOutcomeDto = z.infer<typeof ProvisionVibeClusterOutcomeSchema>;
